'use client';
import http from '@/utils/httpClient';
import { toaster } from '@/utils/toast';
import { Action, Thunk, action, thunk } from 'easy-peasy';

export interface advisorModel {
  phone: string;
  company: string;
  role: string;
  crd_number: string;
  linkedin_url: string;
  firm: string;
  duties: string;
  crm_value: string;
  crm_name: string;
  about: string;
  location: string;
}

export interface providerModel {
  phone: string;
  experience: string;
  hourType: string;
  startDate: string;
  role: string;
  linkedInUrl: string;
  brokerValue: boolean;
  brokerName: string;
  skills: object;
  cv: File | undefined;
  policyDoc?: File | undefined;
  cyberInsurancePolicy: boolean;
  about: string;
  location: string;
}

export interface personalInfo {
  first_name: string | undefined;
  last_name: string | undefined;
  email: string | undefined;
  phone: string | undefined;
  location: string | undefined;
}

export interface professionalInfo {
  experience: string;
  hourType: string;
  role: string;
  startDate: string;
  linkedInUrl: string;
  brokerValue: boolean;
  brokerName: string;
  policyDoc: File;
  cv: File;
  cyberInsurancePolicy: boolean;
}

export interface companyInfo {
  company: string;
  role: string;
  firmAffiliation: string;
  linkedInUrl: string;
  crmValue: string;
  crmName: string;
  crd_number: string;
}

export interface aboutInfo {
  about: string;
}

export interface dutiesInfo {
  duty: string;
}

export interface skillsInfo {
  skills: string[];
}

export interface IProviderSetUp {
  personalInfo: personalInfo | undefined;
  professionalInfo: professionalInfo | undefined;
  skillsInfo: skillsInfo | undefined;
  aboutInfo: aboutInfo | undefined;

  providerModel: providerModel | undefined;

  setPersonalInfo: Action<IProviderSetUp, personalInfo>;
  setProfessionalInfo: Action<IProviderSetUp, professionalInfo>;
  setSkillsInfo: Action<IProviderSetUp, skillsInfo>;
  setAboutInfo: Action<IProviderSetUp, aboutInfo>;
  setProviderModel: Action<IProviderSetUp, providerModel>;

  createProvider: Thunk<IProviderSetUp, providerModel>;
}

export interface IAdvisorSetUp {
  personalInfo: personalInfo | undefined;
  companyInfo: companyInfo | undefined;
  dutiesInfo: dutiesInfo | undefined;
  aboutInfo: aboutInfo | undefined;

  advisorModel: advisorModel | undefined;

  setPersonalInfo: Action<IAdvisorSetUp, personalInfo>;
  setCompanyInfo: Action<IAdvisorSetUp, companyInfo>;
  setDutiesInfo: Action<IAdvisorSetUp, dutiesInfo>;
  setAboutInfo: Action<IAdvisorSetUp, aboutInfo>;
  setAdvisorModel: Action<IAdvisorSetUp, advisorModel>;

  createAdvisor: Thunk<IAdvisorSetUp, advisorModel>;
}

const AdvisorModel: IAdvisorSetUp = {
  advisorModel: undefined,
  personalInfo: undefined,
  companyInfo: undefined,
  dutiesInfo: undefined,
  aboutInfo: undefined,

  setPersonalInfo: action((action, payload) => {
    action.personalInfo = payload;
  }),

  setCompanyInfo: action((action, payload) => {
    action.companyInfo = payload;
  }),

  setDutiesInfo: action((action, payload) => {
    action.dutiesInfo = payload;
  }),

  setAboutInfo: action((action, payload) => {
    action.aboutInfo = payload;
  }),

  setAdvisorModel: action((action, payload) => {
    action.advisorModel = payload;
  }),

  createAdvisor: thunk(async (_action, payload) => {
    // The previous shape was `await http().post(...).then().catch()` --
    // empty .then()/.catch() are pass-through, so we removed them. Errors
    // still propagate to the caller (the test in test/InfoSetupModel.test.ts
    // pins this), success returns the AxiosResponse.
    return http().post('/advisor/profile', payload);
  }),
};

const ProviderModel: IProviderSetUp = {
  providerModel: undefined,
  personalInfo: undefined,
  professionalInfo: undefined,
  skillsInfo: undefined,
  aboutInfo: undefined,

  setPersonalInfo: action((action, payload) => {
    action.personalInfo = payload;
  }),

  setProfessionalInfo: action((action, payload) => {
    action.professionalInfo = payload;
  }),

  setSkillsInfo: action((action, payload) => {
    action.skillsInfo = payload;
  }),

  setAboutInfo: action((action, payload) => {
    action.aboutInfo = payload;
  }),

  setProviderModel: action((action, payload) => {
    action.providerModel = payload;
  }),

  createProvider: thunk(async (_action, payload) => {
    const reqBody = new FormData();
    reqBody.append('phone', payload.phone);
    reqBody.append('experience', payload.experience);
    reqBody.append('hourType', payload.hourType);
    reqBody.append('startDate', payload.startDate);
    reqBody.append('role', payload.role);
    reqBody.append('linkedInUrl', payload.linkedInUrl);
    reqBody.append('brokerValue', JSON.stringify(payload.brokerValue));
    reqBody.append('cyberInsurancePolicy', JSON.stringify(payload.cyberInsurancePolicy));
    reqBody.append('brokerName', payload.brokerName);
    reqBody.append('skills', JSON.stringify(payload.skills));
    reqBody.append('about', payload.about);
    reqBody.append('location', payload.location);
    payload.cv !== undefined ? reqBody.append('cv', payload.cv) : null;
    payload.policyDoc !== undefined ? reqBody.append('policyDoc', payload.policyDoc) : null;
    const response = await http({ 'Content-Type': 'multipart/form-data' })
      .post('/provider/profile', reqBody)
      .then(() => {
        toaster('success', 'Account setup completed');
        return 'success';
      })
      .catch(() => {
        toaster('error', 'Error in setting up account');
        return 'error';
      });
    return response;
  }),
};

export { AdvisorModel, ProviderModel };
